import React, { Component } from 'react';
import PropTypes from 'prop-types';

import SingleSelectInput from './SingleSelectInput';
import DateTimeInput from './DateTimeInput';

export default class RoomBookingSearchInput extends Component {
  static propTypes = {
    locationData: PropTypes.object,
    locationId: PropTypes.string,
    date: PropTypes.string,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    inputChangeCallback: PropTypes.func,
    invalidInputCallback: PropTypes.func,
    timezone: PropTypes.string,
  }

  constructor(props) {
    super(props);
    const timezone = this.props.timezone;

    this.state = {
      date: moment(this.props.date).tz(timezone).format(),
      startTime: moment(this.props.startTime).tz(timezone).format(),
      endTime: moment(this.props.endTime).tz(timezone).format(),
      locationId: this.props.locationId,
      showSearch: false,
      timezone,
    };
  }

  onChangeDate = (value) => {
    const date = value || this.props.date; // If null, change to the default date
    const newDate = moment(date).tz(this.state.timezone);
    this.setState({
      date: newDate.format(),
      startTime: this.timeWithCorrectDate(this.state.startTime, newDate),
      endTime: this.timeWithCorrectDate(this.state.endTime, newDate),
    }, this.handleSearchChange);
  }

  onChangeStartTime = (value) => {
    const time = value || this.props.startTime; // If null, change to the default start time
    const duration = moment(this.state.endTime).diff(this.state.startTime, 'minutes');
    const startTime = this.timeWithCorrectDate(time, this.state.date);
    // Keep the duration difference constant between start and end time
    const endTime = moment(startTime).add(duration, 'minutes').format();
    this.setState({ startTime, endTime }, this.handleSearchChange);
  }

  onChangeEndTime = (value) => {
    const time = value || this.props.endTime; // If null, change to the default end time
    this.setState({ endTime: this.timeWithCorrectDate(time, this.state.date) }, this.handleSearchChange);
  }

  onChangeLocation = (location) => {
    const newTimezone = this.props.locationData[location.id].timezone;
    this.setState({
      timezone: newTimezone,
      locationId: location.id,
      // States should be immutable, so here we create new moment objects instead of
      // calling .tz on the existing ones (tainting them)
      date: moment(this.state.date).tz(newTimezone).format(),
      startTime: moment(this.state.startTime).tz(newTimezone).format(),
      endTime: moment(this.state.endTime).tz(newTimezone).format(),
    }, this.handleSearchChange);
  }

  handleSearchChange = () => {
    if (this.isValid()) {
      this.props.inputChangeCallback(
        this.state.locationId,
        this.state.date,
        this.state.startTime,
        this.state.endTime,
        this.state.timezone);
    } else {
      this.props.invalidInputCallback();
    }
  }

  /**
  * handleEnterKey() allows typing in the input fields but doesn't submit the
  * form when the enter key is pressed (instead it deselects the input)
  */
  handleEnterKey = (event) => {
    if (event.which === 13) {
      event.target.blur();
      event.target.focus();
    }
  }

  /**
  * timeWithCorrectDate() merges the hour and minute in the `time` param with
  * the day, month, and year of the `date` param, and returns a string
  * representing the result of that merge
  * @return {String}
  */
  timeWithCorrectDate(time, date) {
    const newDate = moment(date).tz(this.state.timezone);
    const newDateHash = { year: newDate.year(), month: newDate.month(), date: newDate.date() };
    return moment(time)
      .tz(this.state.timezone)
      .set(newDateHash)
      .format();
  }

  /**
  * displayDuration() returns a formatted duration
  * @return {String}
  */
  displayDuration() {
    const totalMinutes = moment(this.state.endTime).diff(this.state.startTime, 'minutes');
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    let duration = '';
    if (totalMinutes > 0) {
      if (hours) {
        duration += `${hours} hour`;
      }
      if (hours > 1) {
        duration += 's';
      }
      if (hours && minutes) {
        duration += ', ';
      }
      if (minutes) {
        duration += `${minutes} minutes`;
      }
    }
    return duration;
  }

  getLocationArray() {
    if (this.locationArray) {
      return this.locationArray;
    }
    function* objectEntries(obj) {
      // In ES6, you can use strings or symbols as property keys,
      // Reflect.ownKeys() retrieves both
      const propKeys = Reflect.ownKeys(obj);

      for (const propKey of propKeys) {
        yield [propKey, obj[propKey]];
      }
    }
    this.locationArray = [];
    for (const [key, value] of objectEntries(this.props.locationData)) {
      this.locationArray.push({ name: value.name, id: key });
    }
    return this.locationArray;
  }

  /**
  * VALIDATIONS
  *
  * isValid() runs the validations of each of the date fields and determines
  * if form is valid
  *
  * @return {Bool}
  */
  isValid() {
    return (this.dateValidations().valid && this.startTimeValidations().valid && this.endTimeValidations().valid);
  }

  /**
  * dateValidations() returns an object with a key `valid` indicating whether the
  * date validations passed and a key `error` indicating why they failed (if they failed)
  * @return {Object}
  */
  dateValidations() {
    let response = {};
    if (!moment(this.state.date).isValid()) {
      // Moment couldn't parse date
      response = { valid: false, error: "Huh, we didn't understand that" };
    } else if (new Date(this.state.date).setHours(23) < new Date().setHours(0)) {
      // Date is in the past
      response = { valid: false, error: `Time travel not supported (today is ${moment().format('MMMM D, YYYY')})` };
    } else {
      response = { valid: true, error: '' };
    }
    return response;
  }

  /**
  * startTimeValidations() returns an object with a key `valid` indicating whether the
  * start time validations passed and a key `error` indicating why if they failed
  * @return {Object}
  */
  startTimeValidations() {
    let response = {};
    if (!moment(this.state.startTime).isValid()) {
      // Moment couldn't parse date
      response = { valid: false, error: "Huh, we didn't understand that" };
    } else if (!this.isHalfHourIncrement(this.state.startTime)) {
      // Start Time isn't a 30 minute increment
      response = { valid: false, error: 'Bookings start on the hour or half hour' };
    } else if (moment(this.state.startTime).isSameOrAfter(this.state.endTime)) {
      // Start Time is after End Time
      response = { valid: false, error: 'Must start before end time' };
    } else if (this.dateValidations().valid && !this.isStartTimeInFuture()) {
      // There is no error on Date and Start Time is in the future
      response = {
        valid: false,
        error: `Time travel not supported (it\'s ${moment().tz(this.state.timezone).format('h:mm a')})`,
      };
    } else {
      response = { valid: true, error: '' };
    }
    return response;
  }

  /**
  * endTimeValidations() returns an object with a key `valid` indicating whether the
  * end time validations passed and a key `error` indicating why if they failed
  * @return {Object}
  */
  endTimeValidations() {
    let response = {};
    if (!moment(this.state.endTime).isValid()) {
      response = { valid: false, error: "Huh, we didn't understand that" };
    } else if (!this.isHalfHourIncrement(this.state.endTime)) {
      // Start Time isn't a 30 minute increment
      response = { valid: false, error: 'Bookings start on the hour or half hour' };
    } else {
      response = { valid: true, error: '' };
    }
    return response;
  }

  /**
  * isHalfHourIncrement() returns a boolean indicating whether the time ends in
  * 30 or 0
  * @return {Bool}
  */
  isHalfHourIncrement(date) {
    let valid = true;
    if (moment(date).minutes() % 30 !== 0) {
      valid = false;
    }
    return valid;
  }

  /**
  * startTimeInFuture() returns whether the start time is in the future
  * @return {Bool}
  */
  isStartTimeInFuture() {
    let minutes = 0;
    if (moment().minutes() >= 30) {
      minutes = 30;
    }

    return moment(this.state.startTime).isSameOrAfter(moment().startOf('hour').add(minutes, 'minutes'));
  }

  /**
  * When a user selects an alternative time due to rooms not being available, the component
  * must update to reflect the new search. However, because React sends new props to a child component
  * when parent state changes (and the child uses the parent state for it's props), we need to make
  * sure that the props actually changed before we set the state for them.
  */
  componentWillReceiveProps(nextProps) {
    for (const prop in nextProps) {
      if (nextProps[prop] !== this.props[prop]) {
        this.setState({ date: nextProps.date, startTime: nextProps.startTime, endTime: nextProps.endTime });
        break;
      }
    }
  }

  render() {
    // Show either an error or the duration under the end time
    let endTimeText = <span className="error">{this.endTimeValidations().error}</span>;
    if (this.isValid()) {
      endTimeText = <span className="hint">{this.displayDuration()}</span>;
    }

    return (
      <div>
        <div className="Form u-pt-5">
          <div className="u-hintText u-mb-2">Times are displayed in the time zone of the campus</div>
          <form>
            <div className="md:u-flex md:u-flex-row md:u-justify-between">
              <div className="md:u-w-1/4">
                <span className="hint">At</span>
                <SingleSelectInput
                  data={this.getLocationArray()}
                  defaultValue={this.props.locationId}
                  textField="name"
                  valueField="id"
                  name="booking[location]"
                  onChange={this.onChangeLocation}
                />
              </div>

              <div className="md:u-w-1/4">
                <span className="hint">On</span>
                <DateTimeInput
                  useTimezone={this.state.timezone}
                  onKeyDown={this.handleEnterKey}
                  defaultValue={this.state.date}
                  time={false}
                  onChange={this.onChangeDate}
                />
                <span className="error">{this.dateValidations().error}</span>
              </div>

              <div className="md:u-w-1/5">
                <span className="hint">From</span>
                <DateTimeInput
                  useTimezone={this.state.timezone}
                  name="booking[startTime]"
                  defaultValue={this.state.startTime}
                  onKeyDown={this.handleEnterKey}
                  onChange={this.onChangeStartTime}
                  date={false}
                />
                <span className="error">{this.startTimeValidations().error}</span>
              </div>

              <div className="md:u-w-1/5">
                <span className="hint">To</span>
                <DateTimeInput
                  useTimezone={this.state.timezone}
                  name="booking[endTime]"
                  defaultValue={this.state.endTime}
                  onKeyDown={this.handleEnterKey}
                  onChange={this.onChangeEndTime}
                  date={false}
                />
              {endTimeText}
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }
}
